import React from 'react';

interface AppShellProps {
  topBar: React.ReactNode;
  toolsDock: React.ReactNode;
  stage: React.ReactNode;
  inspector: React.ReactNode;
  statusBar: React.ReactNode;
  banners?: React.ReactNode;
  modals?: React.ReactNode;
  /**
   * Whether the right inspector is open as a slide-over drawer.
   * Only affects the narrow-viewport (sm, <768px) behavior. On wider
   * viewports the inspector is a static column regardless of this
   * value. AppShell writes the value as `data-inspector-open` on the
   * shell so CSS can slide the drawer in/out.
   */
  inspectorOpen?: boolean;
  /**
   * Toggle the inspector drawer. Used by the topbar button on narrow
   * viewports and by clicking the backdrop.
   */
  onToggleInspector?: () => void;
}

/** Editor chrome: top bar + 3-column workspace + status bar.
 *
 * The 3-column layout is the operate composition: tools dock (left),
 * PDF stage (center), properties inspector (right). At md (<1024px)
 * the tools dock is hidden (its controls live in the topbar segment).
 * At sm (<768px) the inspector becomes a slide-over drawer that the
 * user opens from the topbar. The 3 jobs — place ink / inspect
 * properties / watch workflow — survive every breakpoint.
 */
export const AppShell: React.FC<AppShellProps> = ({
  topBar,
  toolsDock,
  stage,
  inspector,
  statusBar,
  banners,
  modals,
  inspectorOpen = false,
  onToggleInspector,
}) => (
  <div
    className="editor-shell"
    data-inspector-open={inspectorOpen ? 'true' : 'false'}
  >
    {topBar}
    {banners}
    <div className="editor-workspace">
      <div className="editor-tools-dock">{toolsDock}</div>
      <div className="editor-stage">{stage}</div>
      <div className="editor-inspector" id="editor-inspector">{inspector}</div>
    </div>
    {statusBar}
    {/* Backdrop is only visible (CSS-controlled) at <768px. The
        onClick dismisses the drawer; ESC handling lives in
        SigningWorkspace where the open state is owned. */}
    {onToggleInspector && (
      <div
        className="editor-inspector-backdrop"
        onClick={onToggleInspector}
        aria-hidden="true"
      />
    )}
    {modals}
  </div>
);
